import { Product, Stock } from "interfaces";
import cls from "./productGallery.module.scss";
import { useEffect, useMemo, useRef, useState } from "react";
import SwiperType, { FreeMode, Navigation, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import Image from "next/image";

type Props = {
  data?: Partial<Product>;
  selectedStock?: Stock;
};

export default function ProductGallery({ data, selectedStock }: Props) {
  const allGalleries = useMemo(() => {
    const allStockImages =
      data?.stocks?.flatMap((stock) => stock?.galleries || []) || [];
    return [...(data?.galleries || []), ...allStockImages];
  }, [data]);

  const currentImageIndex = allGalleries?.findIndex(
    (gallery) => gallery?.loadable_id === selectedStock?.id,
  );

  const [activeIndex, setActiveIndex] = useState<number | undefined>(
    currentImageIndex === -1 ? 0 : currentImageIndex,
  );
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
  const mainSwiperRef = useRef<{ swiper: SwiperType }>(null);

  const handleSlideChange = () => {
    setActiveIndex(mainSwiperRef.current?.swiper?.realIndex);
  };

  useEffect(() => {
    if (currentImageIndex && currentImageIndex >= 0) {
      mainSwiperRef.current?.swiper.slideTo(currentImageIndex);
      thumbsSwiper?.slideTo(currentImageIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentImageIndex]);

  return (
    <div className={cls.container}>
      <div className={cls.thumbsContainer}>
        <Swiper
          onSwiper={setThumbsSwiper}
          spaceBetween={10}
          slidesPerView="auto"
          freeMode
          watchSlidesProgress
          modules={[FreeMode, Navigation, Thumbs]}
          breakpoints={{
            1024: {
              direction: "vertical",
            },
            0: {
              direction: "horizontal",
            },
          }}
          className={cls.thumbsSwiper}
        >
          {allGalleries.map((gallery, index) => (
            <SwiperSlide key={gallery?.id} className={cls.thumbSlide}>
              <div
                className={`${cls.thumbImageContainer} ${
                  index === activeIndex ? cls.active : ""
                }`}
              >
                <Image
                  src={gallery?.path || ""}
                  alt={data?.translation?.title || "image"}
                  height={72}
                  width={72}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className={cls.mainContainer}>
        <Swiper
          ref={mainSwiperRef}
          spaceBetween={27}
          modules={[FreeMode, Navigation, Thumbs]}
          thumbs={{
            swiper:
              thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
          }}
          className={cls.mainSwiper}
          slidesPerView={1}
          onSlideChange={handleSlideChange}
        >
          {allGalleries.map((gallery) => (
            <SwiperSlide key={gallery?.id} className={cls.mainSlide}>
              <div className={cls.mainImageContainer}>
                <Image
                  src={gallery?.path || ""}
                  alt={data?.translation?.title || "image"}
                  fill
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
